/** What one point of a series spans. */
export type StackedBarsBucket = 'day' | 'week' | 'month'

/** One column of the chart: the bucket start (YYYY-MM-DD) and a value per segment. */
export interface StackedBarsPoint<K extends string> {
  date: string
  /** Last day the bucket covers (YYYY-MM-DD); derived from the bucket size when omitted. */
  end?: string
  values: Record<K, number>
}

const DAY_MS = 86_400_000

function utcDate(day: string): Date {
  return new Date(`${day.slice(0, 10)}T00:00:00Z`)
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

/** Sums daily points into Monday-started weeks; each week ends on the last day it has data for. */
export function aggregateWeeks<K extends string>(
  daily: readonly StackedBarsPoint<K>[],
  keys: readonly K[],
): StackedBarsPoint<K>[] {
  const weeks: StackedBarsPoint<K>[] = []
  let currentMonday = ''
  for (const point of daily) {
    const date = utcDate(point.date)
    const monday = isoDay(new Date(date.getTime() - ((date.getUTCDay() + 6) % 7) * DAY_MS))
    const last = weeks.at(-1)
    if (last && monday === currentMonday) {
      for (const key of keys) last.values[key] += point.values[key] ?? 0
      last.end = point.date.slice(0, 10)
      continue
    }
    currentMonday = monday
    const values = {} as Record<K, number>
    for (const key of keys) values[key] = point.values[key] ?? 0
    weeks.push({ date: point.date.slice(0, 10), end: point.date.slice(0, 10), values })
  }
  return weeks
}

function bucketEnd(start: Date, bucket: StackedBarsBucket): Date {
  if (bucket === 'week') return new Date(start.getTime() + 6 * DAY_MS)
  return start
}

/** Short axis label and full tooltip label for a point, e.g. de "14. Okt." / "13.–19. Okt.". */
export function pointLabels(
  point: Pick<StackedBarsPoint<string>, 'date' | 'end'>,
  bucket: StackedBarsBucket,
  locale: string,
): { short: string; long: string } {
  const start = utcDate(point.date)
  if (bucket === 'month') {
    return {
      short: new Intl.DateTimeFormat(locale, {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      }).format(start),
      long: new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(start),
    }
  }
  const dayFormat = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
  const short = dayFormat.format(start)
  if (bucket === 'day') return { short, long: short }
  const end = point.end ? utcDate(point.end) : bucketEnd(start, bucket)
  if (end.getTime() <= start.getTime()) return { short, long: short }
  return { short, long: dayFormat.formatRange(start, end) }
}

export function sumValues<K extends string>(values: Record<K, number>, keys: readonly K[]): number {
  let total = 0
  for (const key of keys) total += values[key] ?? 0
  return total
}

interface BarGeometry {
  gap: number
  barWidth: number
}

/** Fits `count` bars into `width`: the gap gives way before a bar gets thinner than 3px. */
export function barGeometry(width: number, count: number, preferredGap: number): BarGeometry {
  if (count <= 0 || width <= 0) return { gap: 0, barWidth: 0 }
  if (count === 1) return { gap: 0, barWidth: width }
  const gap = Math.max(1, Math.min(preferredGap, (width - 3 * count) / (count - 1)))
  const barWidth = (width - gap * (count - 1)) / count
  if (barWidth >= 1) return { gap, barWidth }
  return { gap: 0, barWidth: width / count }
}
